import { PortalConfigService } from '../../services/portal-config.service';
import { ErrorFoot, RequestIdFrom, StatusFrom } from './request-id';
import { STILL_NOT_LOADING_NOTE } from './state-copy';

/**
 * One page's failed-load bookkeeping for the error panel (PD §5, spec §J4): the foot from the error's own status and request id,
 * the "Still not loading…" note once two loads in a row have failed (P/proto-states.js:228-238 — the prototype shows it after
 * the first retry fails), and the Contact support `mailto:` from `research.support.email` (null while the setting is blank, so
 * the button is omitted).
 */
export class LoadFailures {
    private consecutive = 0;

    /** "Error {status} · ref {id}" for the last failure, or null. */
    Foot: string | null = null;
    /** The retry note, from the second consecutive failure on. */
    Note: string | null = null;
    /** `mailto:` for Contact support, or null (setting blank, not read yet, or the config failed to load). */
    SupportHref: string | null = null;

    /** Records a failure: the foot from this error, the note when the previous load failed too. */
    Record(error: unknown): void {
        this.consecutive++;
        this.Foot = ErrorFoot(StatusFrom(error), RequestIdFrom(error));
        this.Note = this.consecutive >= 2 ? STILL_NOT_LOADING_NOTE : null;
    }

    /** A load succeeded: the streak and the foot clear. */
    Reset(): void {
        this.consecutive = 0;
        this.Foot = null;
        this.Note = null;
    }

    /** Reads the support address from the memoised portal config; a failed read leaves the button omitted. */
    async ReadSupport(config: PortalConfigService | null): Promise<void> {
        if (!config) return;
        try {
            this.SupportHref = SupportMailto((await config.Load()).supportEmail);
        } catch {
            this.SupportHref = null;
        }
    }
}

/** `mailto:{email}` for a configured address; null for a blank or missing one. */
export function SupportMailto(email: string | null): string | null {
    const trimmed = (email ?? '').trim();
    return trimmed.length > 0 ? `mailto:${trimmed}` : null;
}
